import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, Crown, Medal, Trophy } from 'lucide-react';
import { Snapshot, AVP } from '../types';
import { formatCurrency, formatNumber } from '../utils/formatters';
import { MovementIndicator } from './MovementIndicator';
interface SnapshotDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  snapshot: Snapshot | null;
  leaderboard: AVP[];
}
export function SnapshotDetailModal({
  isOpen,
  onClose,
  snapshot,
  leaderboard
}: SnapshotDetailModalProps) {
  if (!snapshot) return null;
  const top3 = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);
  return <AnimatePresence data-id="element-492">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" onClick={onClose} data-id="element-493" />
          <div className="fixed inset-0 flex items-start justify-center z-50 p-4 pointer-events-none overflow-y-auto" data-id="element-494">
            <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96,
          y: 20
        }} className="bg-[#141a2c] rounded-3xl my-8 max-w-5xl w-full shadow-2xl pointer-events-auto border border-white/10 relative overflow-hidden" data-id="element-495">
              {/* Locked / preserved ribbon */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-slate-500/50 via-slate-300/50 to-slate-500/50" data-id="element-496" />

              {/* Header */}
              <div className="flex items-start justify-between p-6 border-b border-white/5" data-id="element-497">
                <div data-id="element-498">
                  <div className="flex items-center gap-2 mb-2" data-id="element-499">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-500/10 border border-slate-500/20 text-slate-400 text-xs font-medium" data-id="element-500">
                      <Lock className="w-3 h-3" data-id="element-501" /> Finalized Snapshot
                    </div>
                    <span className="text-xs text-slate-500" data-id="element-502">
                      Locked {new Date(snapshot.lockedAt!).toLocaleDateString()}
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-white" data-id="element-503">
                    {snapshot.month} {snapshot.year} Leaderboard
                  </h2>
                  <p className="text-sm text-slate-400 mt-1" data-id="element-504">
                    Archived performance — preserved exactly as recorded at
                    month-end
                  </p>
                </div>
                <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1" data-id="element-505">
                  <X className="w-5 h-5" data-id="element-506" />
                </button>
              </div>

              {/* Summary KPIs */}
              <div className="grid grid-cols-3 gap-4 p-6 border-b border-white/5" data-id="element-507">
                <div className="bg-black/20 rounded-xl p-4 border border-white/5" data-id="element-508">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-509">
                    Leaders Included
                  </p>
                  <p className="text-xl font-bold text-white" data-id="element-510">
                    {leaderboard.length}
                  </p>
                </div>
                <div className="bg-black/20 rounded-xl p-4 border border-white/5" data-id="element-511">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-512">Top Avg Revenue</p>
                  <p className="text-xl font-bold text-emerald-400" data-id="element-513">
                    {leaderboard.length > 0 ? formatCurrency([...leaderboard].sort((a, b) => b.avgRevenue - a.avgRevenue)[0].avgRevenue) : '—'}
                  </p>
                </div>
                <div className="bg-black/20 rounded-xl p-4 border border-white/5" data-id="element-514">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-515">
                    Period Avg Revenue
                  </p>
                  <p className="text-xl font-bold text-white" data-id="element-516">
                    {leaderboard.length > 0 ? formatCurrency(leaderboard.reduce((s, a) => s + a.avgRevenue, 0) / leaderboard.length) : '—'}
                  </p>
                </div>
              </div>

              {/* Top 3 podium - compact, podium-ordered (2nd, 1st, 3rd) */}
              <div className="p-6 border-b border-white/5" data-id="element-517">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-518">
                  <Trophy className="w-4 h-4 text-yellow-400" data-id="element-519" /> Top Performers
                </h3>
                <div className="grid grid-cols-3 gap-3 items-end" data-id="element-520">
                  {[top3[1], top3[0], top3[2]].filter(Boolean).map(avp => {
                const isFirst = avp.rank === 1;
                const isSecond = avp.rank === 2;
                const cardStyle = isFirst ? 'bg-gradient-gold' : isSecond ? 'bg-gradient-silver' : 'bg-gradient-bronze';
                const Icon = isFirst ? Crown : Medal;
                const iconColor = isFirst ? 'text-yellow-400' : isSecond ? 'text-slate-300' : 'text-orange-400';
                const podiumOffset = isFirst ? '-translate-y-6' : isSecond ? '-translate-y-2' : '';
                return <div key={avp.id} className={`rounded-2xl p-4 border flex items-center gap-3 ${cardStyle} ${podiumOffset}`} data-id="element-521">
                        <div className="w-10 h-10 rounded-full bg-[#0f1423] border border-white/10 flex items-center justify-center text-sm font-bold shrink-0" data-id="element-522">
                          {avp.initials}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-523">
                          <div className="flex items-center gap-1" data-id="element-524">
                            <Icon className={`w-3.5 h-3.5 ${iconColor}`} data-id="element-525" />
                            <span className="text-xs text-slate-400" data-id="element-526">
                              #{avp.rank}
                            </span>
                          </div>
                          <p className="text-sm font-semibold text-white truncate" data-id="element-527">
                            {avp.name}
                          </p>
                          <p className="text-xs text-slate-400 truncate" data-id="element-528">
                            {formatCurrency(avp.avgRevenue)} avg
                          </p>
                        </div>
                      </div>;
              })}
                </div>
              </div>

              {/* Full table — ranked by avg revenue */}
              <div className="p-6 max-h-[400px] overflow-y-auto" data-id="element-529">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4" data-id="element-530">
                  Full Standings
                </h3>
                <table className="w-full text-left" data-id="element-531">
                  <thead data-id="element-532">
                    <tr className="border-b border-white/10 text-xs uppercase text-slate-500" data-id="element-533">
                      <th className="py-2 pr-3" data-id="element-534">Rank</th>
                      <th className="py-2 pr-3" data-id="element-535">Leader</th>
                      <th className="py-2 pr-3" data-id="element-536">Division</th>
                      <th className="py-2 pr-3 text-right" data-id="element-537">LO Count</th>
                      <th className="py-2 pr-3 text-right" data-id="element-538">Loans</th>
                      <th className="py-2 pr-3 text-right whitespace-nowrap" data-id="element-539">
                        Total Comm. (Wire)
                      </th>
                      <th className="py-2 pr-3 text-right" data-id="element-540">Avg Revenue</th>
                      <th className="py-2 text-right" data-id="element-541">Move</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5" data-id="element-542">
                    {[...leaderboard].sort((a, b) => b.avgRevenue - a.avgRevenue).map((avp, idx) => {
                  const displayRank = idx + 1;
                  return <tr key={avp.id} className="hover:bg-white/[0.02] transition-colors" data-id="element-543">
                            <td className="py-3 pr-3" data-id="element-544">
                              <span className={`font-semibold ${displayRank <= 3 ? 'text-yellow-400' : 'text-slate-300'}`} data-id="element-545">
                                {displayRank}
                              </span>
                            </td>
                            <td className="py-3 pr-3" data-id="element-546">
                              <div className="flex items-center gap-2" data-id="element-547">
                                <div className="w-7 h-7 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-300 text-xs font-semibold" data-id="element-548">
                                  {avp.initials}
                                </div>
                                <span className="text-sm text-white font-medium" data-id="element-549">
                                  {avp.name}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 pr-3 text-sm text-slate-400" data-id="element-550">
                              {avp.division}
                            </td>
                            <td className="py-3 pr-3 text-sm text-slate-300 text-right" data-id="element-551">
                              {avp.loCount}
                            </td>
                            <td className="py-3 pr-3 text-sm text-slate-300 text-right" data-id="element-552">
                              {formatNumber(avp.loansClosed)}
                            </td>
                            <td className="py-3 pr-3 text-sm text-white text-right whitespace-nowrap" data-id="element-553">
                              {formatCurrency(avp.totalRevenue)}
                            </td>
                            <td className="py-3 pr-3 text-sm text-emerald-400 font-medium text-right whitespace-nowrap" data-id="element-554">
                              {formatCurrency(avp.avgRevenue)}
                            </td>
                            <td className="py-3 text-right" data-id="element-555">
                              <div className="flex justify-end" data-id="element-556">
                                <MovementIndicator currentRank={avp.rank} previousRank={avp.previousRank} data-id="element-557" />
                              </div>
                            </td>
                          </tr>;
                })}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-white/5 flex items-center justify-between bg-black/10" data-id="element-558">
                <p className="text-xs text-slate-500 flex items-center gap-2" data-id="element-559">
                  <Lock className="w-3 h-3" data-id="element-560" />
                  This snapshot is permanently preserved and cannot be modified
                </p>
                <button onClick={onClose} className="px-4 py-2 rounded-lg glass-panel hover:bg-white/10 text-white text-sm font-medium transition-colors" data-id="element-561">
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}